import { isISODateTimeString } from '@gat-ship/types'
import { Temporal } from '@js-temporal/polyfill'
import { parsePhoneNumber } from 'awesome-phonenumber'

export const EM_DASH = '\u2014'

export type DateTimeStyle = 'short' | 'medium' | 'long' | 'full'

export interface FormatDateTimeOptions {
  hideYearIfCurrent?: boolean
}

export interface FormatNumberOptions {
  decimals?: number
  minDecimals?: number
  maxDecimals?: number
}

export interface FormatCurrencyOptions {
  decimals?: number
}

export interface FormatPercentOptions {
  decimals?: number
}

export interface PluralForms {
  one: string
  other: string
  zero?: string
  two?: string
  few?: string
  many?: string
}

function isValidISODate(value: string | null | undefined): value is string {
  if (!value) return false
  return isISODateTimeString(value)
}

/**
 * Pins the display language to English while preserving the locale's region
 * (and thus its date-field ordering / separators, where the engine honors
 * them). `nb-NO` → `en-NO`, `de` → `en`.
 *
 * Used by the date/time formatters so month and weekday names are always
 * English regardless of the user's locale, without otherwise changing the
 * regional format. `Intl` couples language and region in one locale string,
 * so swapping only the language subtag is the only way to decouple them.
 */
function toEnglishLocale(locale: string): string {
  try {
    const { region } = new globalThis.Intl.Locale(locale)
    return region ? `en-${region}` : 'en'
  } catch {
    return 'en'
  }
}

function isValidNumber(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && !Number.isNaN(value)
}

/**
 * Day + month options used when the year is omitted (current-year dates).
 * `long`/`full` spell the month out; everything else abbreviates it. Shared
 * by `formatDateStandalone` and `formatDateTimeStandalone` so they stay in
 * sync.
 */
function currentYearDateOptions(
  style: DateTimeStyle
): globalThis.Intl.DateTimeFormatOptions {
  switch (style) {
    case 'long':
    case 'full':
      return { day: '2-digit', month: 'long' }
    default:
      return { day: '2-digit', month: 'short' }
  }
}

export function formatDateStandalone(
  value: string | null | undefined,
  locale: string,
  timeZone: string,
  style: DateTimeStyle = 'medium',
  options?: FormatDateTimeOptions
): string {
  if (!isValidISODate(value)) return EM_DASH

  const hideYear = options?.hideYearIfCurrent ?? true
  const zonedDate = Temporal.Instant.from(value).toZonedDateTimeISO(timeZone)
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const isCurrentYear = hideYear && zonedDate.year === now.year

  const dateOptions = isCurrentYear
    ? currentYearDateOptions(style)
    : { dateStyle: style }

  return zonedDate.toLocaleString(toEnglishLocale(locale), dateOptions)
}

/**
 * Formats a date range with shared fields collapsed, via
 * `Intl.DateTimeFormat.formatRange`: `16 Jun – 01 Jul` across months,
 * `03 – 05 Jun` within one month, a single `03 Jun` when both ends
 * fall on the same day. Year handling matches `formatDateStandalone`
 * (`hideYearIfCurrent`, default on): hidden when BOTH endpoints are in
 * the current year, otherwise rendered and collapsed when shared.
 * Returns an em dash when either endpoint is missing or invalid.
 */
export function formatDateRangeStandalone(
  from: string | null | undefined,
  to: string | null | undefined,
  locale: string,
  timeZone: string,
  style: DateTimeStyle = 'medium',
  options?: FormatDateTimeOptions
): string {
  if (!isValidISODate(from) || !isValidISODate(to)) return EM_DASH

  const hideYear = options?.hideYearIfCurrent ?? true
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const fromZoned = Temporal.Instant.from(from).toZonedDateTimeISO(timeZone)
  const toZoned = Temporal.Instant.from(to).toZonedDateTimeISO(timeZone)
  const isCurrentYear =
    hideYear && fromZoned.year === now.year && toZoned.year === now.year

  const dateOptions: globalThis.Intl.DateTimeFormatOptions = isCurrentYear
    ? currentYearDateOptions(style)
    : { dateStyle: style }

  return new globalThis.Intl.DateTimeFormat(toEnglishLocale(locale), {
    timeZone,
    ...dateOptions
  }).formatRange(
    new Date(fromZoned.epochMilliseconds),
    new Date(toZoned.epochMilliseconds)
  )
}

export function formatDateTimeStandalone(
  value: string | null | undefined,
  locale: string,
  timeZone: string,
  style: DateTimeStyle = 'medium',
  options?: FormatDateTimeOptions
): string {
  if (!isValidISODate(value)) return EM_DASH

  const hideYear = options?.hideYearIfCurrent ?? true
  const zonedDate = Temporal.Instant.from(value).toZonedDateTimeISO(timeZone)
  const now = Temporal.Now.zonedDateTimeISO(timeZone)
  const isCurrentYear = hideYear && zonedDate.year === now.year

  type DTFOptions = globalThis.Intl.DateTimeFormatOptions

  const dateOptions: DTFOptions = isCurrentYear
    ? currentYearDateOptions(style)
    : { dateStyle: style }

  const timeOptions: DTFOptions = isCurrentYear
    ? { hour: '2-digit', minute: '2-digit' }
    : { timeStyle: 'short' }

  return zonedDate
    .toLocaleString(toEnglishLocale(locale), {
      hour12: false,
      ...dateOptions,
      ...timeOptions
    })
    .toString()
    .replace(',', '')
}

export function formatTimeStandalone(
  value: string | null | undefined,
  locale: string,
  timeZone: string,
  style: DateTimeStyle = 'short'
): string {
  if (!isValidISODate(value)) return EM_DASH
  const zonedDate = Temporal.Instant.from(value).toZonedDateTimeISO(timeZone)
  return zonedDate.toLocaleString(toEnglishLocale(locale), {
    hour12: false,
    timeStyle: style
  })
}

export function formatNumberStandalone(
  value: number | null | undefined,
  locale: string,
  options?: FormatNumberOptions
): string {
  if (!isValidNumber(value)) return EM_DASH
  return new globalThis.Intl.NumberFormat(locale, {
    minimumFractionDigits: options?.minDecimals ?? options?.decimals,
    maximumFractionDigits: options?.maxDecimals ?? options?.decimals
  }).format(value)
}

export function formatCurrencyStandalone(
  value: number | null | undefined,
  currencyCode: string,
  locale: string,
  options?: FormatCurrencyOptions
): string {
  if (!isValidNumber(value)) return EM_DASH
  return new globalThis.Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: options?.decimals,
    maximumFractionDigits: options?.decimals
  }).format(value)
}

export function formatPercentStandalone(
  value: number | null | undefined,
  locale: string,
  options?: FormatPercentOptions
): string {
  if (!isValidNumber(value)) return EM_DASH
  return new globalThis.Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: options?.decimals,
    maximumFractionDigits: options?.decimals
  }).format(value)
}

export function formatPhoneNumberStandalone(
  value: string | null | undefined
): string {
  if (!value) return EM_DASH
  const parsed = parsePhoneNumber(value)
  if (parsed.valid) {
    const type = 'international' as const
    return parsed.number[type] || value
  }
  return value
}

export function formatPluralStandalone(
  count: number,
  forms: PluralForms,
  locale: string
): string {
  const rules = new globalThis.Intl.PluralRules(locale)
  const category = rules.select(count)

  switch (category) {
    case 'zero':
      return forms.zero ?? forms.other
    case 'one':
      return forms.one
    case 'two':
      return forms.two ?? forms.other
    case 'few':
      return forms.few ?? forms.other
    case 'many':
      return forms.many ?? forms.other
    default:
      return forms.other
  }
}
